import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, Clock, CheckCircle2, XCircle } from 'lucide-react';
import {
  getPadron,
  getPadronDashboard,
  type PadronDashboard,
} from '@lib/services/padrones.service';
import { CardsSkeleton } from '@components/medicus-ui/Loaders';
import { EstadoLegajoBadge } from '@components/EstadoLegajoBadge';
import { estadoLegajoInfo } from '@lib/estado-legajo';

/**
 * Dashboard del padrón para admin Medicus. Vista de funnel + métricas
 * agregadas para entender en qué paso se quedan los legajos del padrón.
 * Visible solo para ADMIN_MEDICUS / SUPERADMIN_MEDICUS (route guard +
 * server-side check en el endpoint).
 *
 * Los LABELS de cada estado salen del catálogo único (estadoLegajoInfo). El
 * color de las barras del funnel se mantiene local (paleta distinta por paso
 * para legibilidad del embudo, no la del badge).
 */
const ESTADO_COLOR: Record<string, string> = {
  pendiente_validacion_medicus: '#92400E',
  rechazado_medicus: '#B91C1C',
  creado: '#1D4ED8',
  en_validacion: '#B45309',
  validado_por_titular: '#0E7490',
  en_alta_binary: '#7C3AED',
  dado_de_alta: '#166534',
  rechazado_binary: '#991B1B',
};

const ORDEN_ESTADOS = [
  'pendiente_validacion_medicus',
  'rechazado_medicus',
  'creado',
  'en_validacion',
  'validado_por_titular',
  'en_alta_binary',
  'dado_de_alta',
  'rechazado_binary',
];

export function PadronDashboardPage() {
  const { id = '' } = useParams();

  const padronQ = useQuery({
    queryKey: ['padron', id],
    queryFn: () => getPadron(id),
    enabled: !!id,
  });

  const dashboardQ = useQuery({
    queryKey: ['padron-dashboard', id],
    queryFn: () => getPadronDashboard(id),
    enabled: !!id,
    refetchInterval: 30_000,
  });

  if (dashboardQ.isLoading || padronQ.isLoading) {
    return (
      <section>
        <header className="pageHeader">
          <h1 className="heading-3" style={{ margin: 0 }}>
            Dashboard de legajos
          </h1>
        </header>
        <div style={{ marginTop: 16 }}>
          <CardsSkeleton count={8} />
        </div>
      </section>
    );
  }
  if (dashboardQ.isError || !dashboardQ.data) {
    return (
      <p className="status status-error" style={{ display: 'block' }}>
        No se pudo cargar el dashboard del padrón.
      </p>
    );
  }
  const d = dashboardQ.data;
  const padron = padronQ.data;

  return (
    <section>
      <header className="pageHeader" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Link to={`/padrones/${id}`} className="btn btn-ghost btn--sm" style={{ padding: '4px 10px' }}>
          <ArrowLeft size={14} /> Volver al detalle
        </Link>
        <div>
          <h1 className="heading-3" style={{ margin: 0 }}>
            Dashboard de legajos
          </h1>
          <p className="text-element-mid" style={{ margin: '2px 0 0', fontSize: 13 }}>
            {padron?.nombreArchivo ?? id} · {d.totalLegajos.toLocaleString('es-AR')} legajos
          </p>
        </div>
      </header>

      {/* Funnel principal por estado del legajo */}
      <section className="card" style={{ marginTop: 16 }}>
        <h2 className="heading-5" style={{ marginBottom: 12 }}>Funnel por estado</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {ORDEN_ESTADOS.map((estado) => {
            const count = d.porEstado[estado] ?? 0;
            const pct = d.totalLegajos > 0 ? (count / d.totalLegajos) * 100 : 0;
            return (
              <FunnelRow
                key={estado}
                label={estadoLegajoInfo(estado).label}
                count={count}
                pct={pct}
                color={ESTADO_COLOR[estado] ?? '#475569'}
              />
            );
          })}
        </div>
      </section>

      {/* Sub-funnel: paso del wizard donde quedaron los `en_validacion` */}
      <section className="card" style={{ marginTop: 16 }}>
        <h2 className="heading-5" style={{ marginBottom: 4 }}>
          Detalle de “{estadoLegajoInfo('en_validacion').label}” ({(d.porEstado.en_validacion ?? 0).toLocaleString('es-AR')})
        </h2>
        <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 12 }}>
          Paso del wizard donde se quedó cada titular. Cada legajo cae en el último paso que no completó.
        </p>
        <div className="grid grid-cols-3 gap-md" style={{ gap: 12 }}>
          <KpiCard label="Datos titular" valor={d.desgloseEnValidacion.enDatosTitular} tone="info" />
          <KpiCard label="Datos grupo familiar" valor={d.desgloseEnValidacion.enDatosGF} tone="info" />
          <KpiCard label="Cuestionario de salud" valor={d.desgloseEnValidacion.enCuestionarioSalud} tone="warning" />
          <KpiCard label="Elección de plan" valor={d.desgloseEnValidacion.enEleccionPlan} tone="warning" />
        </div>
      </section>

      {/* Sub-funnel: decisión final de los `validado_por_titular` */}
      <section className="card" style={{ marginTop: 16 }}>
        <h2 className="heading-5" style={{ marginBottom: 4 }}>
          Detalle de “{estadoLegajoInfo('validado_por_titular').label}” ({(d.porEstado.validado_por_titular ?? 0).toLocaleString('es-AR')})
        </h2>
        <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 12 }}>
          Decisión final del titular. <strong>Contacto comercial</strong> es una opción dentro del CTA de MS2 (titulares
          que pidieron asesoramiento).
        </p>
        <div className="grid grid-cols-3 gap-md" style={{ gap: 12 }}>
          <KpiCard label="Plan MS1" valor={d.desgloseValidadoPorTitular.planMs1} tone="info" />
          <KpiCard label="Plan MS2" valor={d.desgloseValidadoPorTitular.planMs2} tone="success" />
          <KpiCard label="Contacto comercial" valor={d.desgloseValidadoPorTitular.contactoComercial} tone="warning" />
        </div>
      </section>

      {/* Renaper */}
      <section className="card" style={{ marginTop: 16 }}>
        <h2 className="heading-5" style={{ marginBottom: 4 }}>
          Renaper — {d.renaper.total.toLocaleString('es-AR')} asociados consultados
        </h2>
        <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 12 }}>
          {d.renaper.titularesConPisos.toLocaleString('es-AR')} titulares con campos pisados por Renaper.
        </p>
        <div className="grid grid-cols-3 gap-md" style={{ gap: 12 }}>
          <KpiCard label="Pendientes" valor={d.renaper.porEstado.pendiente} tone="info" />
          <KpiCard label="Validados" valor={d.renaper.porEstado.validado} tone="success" />
          <KpiCard label="No coinciden" valor={d.renaper.porEstado.no_coincide} tone="warning" />
          <KpiCard label="No encontrados" valor={d.renaper.porEstado.no_encontrado} tone="error" />
          <KpiCard label="Con error" valor={d.renaper.porEstado.error} tone="error" />
        </div>
      </section>

      {/* Próximos a vencer + Observaciones */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
          gap: 16,
          marginTop: 16,
        }}
      >
        <ProximosAVencerCard padronId={id} items={d.proximosAVencer} />
        <ObservacionesCard count={d.observacionesPendientes} />
      </div>

      {/* Rechazos */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
          gap: 16,
          marginTop: 16,
        }}
      >
        <MotivosRechazoCard
          titulo="Top motivos de rechazo — Medicus"
          motivos={d.rechazosMedicus}
          colorBg="#FEF2F2"
          colorBorder="#FECACA"
        />
        <MotivosRechazoCard
          titulo="Top motivos de rechazo — Binary"
          motivos={d.rechazosBinary}
          colorBg="#FEF3C7"
          colorBorder="#FCD34D"
        />
      </div>
    </section>
  );
}

function FunnelRow({
  label,
  count,
  pct,
  color,
}: {
  label: string;
  count: number;
  pct: number;
  color: string;
}) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: 'var(--color-element-high)', fontWeight: 600 }}>
          {label}
        </span>
        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color }}>
          {count.toLocaleString('es-AR')} ({pct.toFixed(1)}%)
        </span>
      </div>
      <div
        style={{
          height: 10,
          background: 'var(--color-surface-alt, #F1F5F9)',
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${Math.max(pct, count > 0 ? 1 : 0)}%`,
            height: '100%',
            background: color,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}

function KpiCard({
  label,
  valor,
  tone,
}: {
  label: string;
  valor: number;
  tone: 'success' | 'warning' | 'error' | 'info';
}) {
  const colors = {
    success: { bg: '#F0FDF4', border: '#BBF7D0', color: '#166534' },
    warning: { bg: '#FEF3C7', border: '#FCD34D', color: '#92400E' },
    error: { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
    info: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E40AF' },
  }[tone];
  return (
    <article
      style={{
        padding: '12px 14px',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
      }}
    >
      <p style={{ fontSize: 12, color: 'var(--color-element-mid)', margin: 0 }}>{label}</p>
      <strong
        style={{ fontSize: 22, color: colors.color, fontVariantNumeric: 'tabular-nums' }}
      >
        {valor.toLocaleString('es-AR')}
      </strong>
    </article>
  );
}

function ProximosAVencerCard({
  padronId,
  items,
}: {
  padronId: string;
  items: PadronDashboard['proximosAVencer'];
}) {
  return (
    <section className="card">
      <h2 className="heading-5" style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={16} /> Próximos a vencer (7 días)
      </h2>
      <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 8 }}>
        Legajos en `creado` o `en_validacion` cuya activación expira pronto.
      </p>
      {items.length === 0 && (
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Ningún legajo vence en los próximos 7 días. ✓
        </p>
      )}
      {items.length > 0 && (
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table className="dataTable" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Legajo</th>
                <th>Estado</th>
                <th>Vence</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.legajoId}>
                  <td>
                    <Link to={`/padrones/${padronId}`} style={{ color: 'var(--color-primary-darker)' }}>
                      {it.numero}
                    </Link>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <EstadoLegajoBadge estado={it.estado} size={11} />
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {new Date(it.expiraAt).toLocaleDateString('es-AR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ObservacionesCard({ count }: { count: number }) {
  return (
    <section className="card">
      <h2 className="heading-5" style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <AlertTriangle size={16} /> Observaciones pendientes
      </h2>
      <p className="text-element-mid" style={{ fontSize: 13, marginBottom: 12 }}>
        Legajos con al menos una observación reportada por el titular sin resolver.
        Bloquean el alta a Binary hasta que RRHH/Medicus las marque como resueltas.
      </p>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          background: count > 0 ? '#FEF2F2' : '#F0FDF4',
          border: `1px solid ${count > 0 ? '#FECACA' : '#BBF7D0'}`,
          borderRadius: 10,
        }}
      >
        {count > 0 ? (
          <XCircle size={28} color="#991B1B" />
        ) : (
          <CheckCircle2 size={28} color="#166534" />
        )}
        <div>
          <strong
            style={{
              fontSize: 26,
              fontVariantNumeric: 'tabular-nums',
              color: count > 0 ? '#991B1B' : '#166534',
            }}
          >
            {count.toLocaleString('es-AR')}
          </strong>
          <p style={{ fontSize: 12, margin: 0, color: 'var(--color-element-mid)' }}>
            {count > 0 ? 'legajos requieren acción' : 'todo al día'}
          </p>
        </div>
      </div>
    </section>
  );
}

function MotivosRechazoCard({
  titulo,
  motivos,
  colorBg,
  colorBorder,
}: {
  titulo: string;
  motivos: Array<{ motivo: string; count: number }>;
  colorBg: string;
  colorBorder: string;
}) {
  return (
    <section className="card">
      <h2 className="heading-5" style={{ marginBottom: 8 }}>{titulo}</h2>
      {motivos.length === 0 && (
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Sin rechazos en este padrón. ✓
        </p>
      )}
      {motivos.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
          {motivos.map((m, i) => (
            <li
              key={`${m.motivo}-${i}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: colorBg,
                border: `1px solid ${colorBorder}`,
                borderRadius: 8,
                fontSize: 13,
              }}
            >
              <span style={{ flex: 1, paddingRight: 8 }}>{m.motivo}</span>
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                {m.count.toLocaleString('es-AR')}
              </strong>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
